import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Redux
import { getSelectedProject } from '../../redux/projects/actions';
import { setAlert } from '../../redux/dashboard/actions';
import { getUser } from '../../redux/user/actions';
import { getSelectedAnalyticsDashboard, getAnalyticsTemplates } from '../../redux/templates/actions';

// Components
import components from '../../components';
import HeaderPDF from '../../components/header/HeaderPDF';
import GenerateReportButton from './generateReportButton';
import CustomWidgets from './customWidgets/customWidgets';
import WidgetGrid from './widgetGrid';
import SidePanel from '../../components/sidepanel';
import Loading from '../../components/loading';

// Utilities
import utils from '../../utils';
import reportUtils from '../../utils/report';
import { getFilteredWidgets } from '../../utils/widgets';
import calendar from '../../utils/calendar';
import API from '../../services/api';
import { getCmsImpressionWidgets, getProjectCmsVendors } from '../../utils/analytics/cmsImpressions';

import './styles.scss';

const { DateTimeFilterBar, AnalyticsSelector } = components;

const AnalyticsContainer = ({ lastUpdated }) => {
  // Sites
  const [sitesProjectId, setSitesProjectId] = useState(null);
  const [sites, setSites] = useState([]); //All sites
  const [siteIds, setSiteIds] = useState([]); //All site IDs

  //Devices
  const [devices, setDevices] = useState([]);
  const [heatmapDevices, setHeatmapDevices] = useState([]);
  const [wifiDevices, setWifiDevices] = useState([]);
  const [appliedFilters, setAppliedFilters] = useDashboardFilters(null);
  const [selectedSolution, setSelectedSolution] = useState(null);

  //Assets
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);

  // Stop using local widget definition
  const [currentWidgets, setCurrentWidgets] = useState({}); // Current list of widgets, get from dashboard template
  const [filteredWidgets, setFilteredWidgets] = useState([]); // Merged array from Selected widgets and default list

  // Flags
  const [hasTable, setHasTable] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [collapseSidebar, setCollapseSidebar] = useState(true);

  // Redux variables
  const user = useSelector(getUser);
  const project = useSelector(getSelectedProject);
  const selectedDashboard = useSelector(getSelectedAnalyticsDashboard);
  const analyticsTemplates = useSelector(getAnalyticsTemplates);

  const dispatch = useDispatch();

  const eventHandlers = useRef([]);
  const oldProjectRef = useRef();
  const oldDashboardRef = useRef();
  const queryFilterLoadRef = useRef({ key: '', promise: null });

  //#region useEffects
  // Update applied filters when project changes
  useEffect(() => {
    const fetchSites = async () => {
      await getQueryFilter();
    };

    setLoadingDashboard(true);
    if (project && user) {
      const startDate = !oldProjectRef.current ? calendar.getDefaultDate('start') : appliedFilters.startDate;
      const endDate = !oldProjectRef.current ? calendar.getDefaultDate('end') : appliedFilters.endDate;

      // Load Filters
      setAppliedFilters({
        startDate,
        endDate,
        timezone: project.preferences.timezone,
        interval: calendar.getIntervalById('day'),
        filterByTime: {},
        filterByWeekday: [],
        selectedDevices: devices,
        wifiDevices,
        heatmapDevices,
        selectedSites: sites,
        selectedMedias: [],
        selectedAdvertisers: [],
        selectedSolution,
        selectedAssets,
        cmsPlayerIds: [],
        // mobile data extra params
        sites: siteIds,
        startTime: startDate, // Naming convention is different on bigQuery
        endTime: endDate // Naming convention is different on bigQuery
      });

      if (project.id !== oldProjectRef.current) {
        fetchSites();
        loadSolution();
      }

      oldProjectRef.current = project.id;
    }

    return () => {
      setLoadingDashboard(false);
    };
  }, [project]);

  // Update widgets after fetching them all
  useEffect(() => {
    const widgetCount = getWidgetCount();
    if (widgetCount > 0 && Object.keys(currentWidgets).length === widgetCount) {
      loadWidgets();
    }
  }, [currentWidgets]);

  // Update widgets when applied filters changes
  useEffect(() => {
    if (appliedFilters) {
      loadWidgets();
    }
    API.refreshTokenWhenNeeded();
  }, [appliedFilters]);

  useEffect(() => {
    if (appliedFilters) {
      setAppliedFilters({ ...appliedFilters, wifiDevices, heatmapDevices });
    }
  }, [heatmapDevices, wifiDevices]);

  useEffect(() => {
    setAppliedFilters(filters => filters && { ...filters, selectedSolution });
  }, [selectedSolution]);

  // Update widgets when applied filters changes
  useEffect(() => {
    setLoadingDashboard(true);
    const fetchSites = async () => {
      if (selectedDashboard && selectedDashboard.id !== oldDashboardRef.current) {
        await getQueryFilter();
        oldDashboardRef.current = selectedDashboard.id;
      }
    };

    const fetchWidgets = async () => {
      if (selectedDashboard && selectedDashboard.id !== oldDashboardRef.current) {
        setHasTable(true);
        setLoadingDashboard(true);
        // Load Widgets
        let widgets = {};

        const widgetsToFetch = [];
        selectedDashboard.rows.forEach(dashboardRow => {
          dashboardRow.widgets.forEach(widgetData => {
            widgetsToFetch.push(widgetData);
          });
        });

        const fetchedWidgets = await Promise.all(
          widgetsToFetch.map(async widgetData => {
            const widget = await API.getWidget(widgetData.url);
            return { ...widget, widgetUrl: widgetData.url };
          })
        );

        fetchedWidgets.forEach(fetchedWidget => {
          widgets = { ...widgets, [fetchedWidget.id]: fetchedWidget };
        });

        setCurrentWidgets(widgets);
        fetchSites();

        oldDashboardRef.current = selectedDashboard.id;
      }
    };

    fetchWidgets();
    loadSolution();

    API.refreshTokenWhenNeeded();
    return () => {
      setLoadingDashboard(false);
    };
  }, [selectedDashboard]);

  useEffect(() => {
    setLoadingDashboard(true);
    if (!sites || sites.length === 0 || !assets) {
      setLoadingDashboard(false);
      return;
    }

    loadDevices(assets);
    setSiteIds(sites.map(site => site.id));
    setAppliedFilters(prevState => {
      return {
        ...prevState,
        sites: sites.map(site => site.id)
      };
    });

    setLoadingDashboard(false);
  }, [sites, assets]);

  useEffect(() => {
    const availableSiteIds = new Set((sites || []).map(site => site.id));
    const selectedAssets = (assets || []).filter(asset => availableSiteIds.has(asset.siteId));
    setSelectedAssets(selectedAssets);
    const cmsPlayerIds = getCmsPlayerIds(selectedAssets);
    setAppliedFilters(prevState => {
      return {
        ...prevState,
        selectedAssets,
        cmsPlayerIds
      };
    });
  }, [assets, sites]);

  useEffect(() => {
    if (appliedFilters) {
      const cmsPlayerIds = getCmsPlayerIds(appliedFilters.selectedAssets || []);
      setAppliedFilters(prevState => {
        return {
          ...prevState,
          cmsPlayerIds
        };
      });
    }
  }, [appliedFilters?.selectedAssets]);

  useEffect(() => {
    setLoadingDashboard(true);
    return () => {
      eventHandlers.current.forEach(handler => {
        window.removeEventListener('requestResult', handler);
        window.removeEventListener('requestError', handler);
      });
      setLoadingDashboard(false);
    };
  }, []);
  //#endregion

  //#region Getters
  const getQueryFilter = async () => {
    if (!project || !selectedDashboard || !selectedDashboard.compatibleDatasources) return;

    const loadKey = `${project.id}:${selectedDashboard.id}`;
    const previousLoad = queryFilterLoadRef.current;
    if (previousLoad.key === loadKey) {
      return previousLoad.promise;
    }

    setDevices([]);
    setSites([]);
    const loadPromise = loadAllSites();
    queryFilterLoadRef.current = { key: loadKey, promise: loadPromise };

    try {
      return await loadPromise;
    } finally {
      if (queryFilterLoadRef.current.promise === loadPromise) {
        queryFilterLoadRef.current = { key: loadKey, promise: null };
      }
    }
  };

  const getWidgetsInPlace = () => {
    if (!selectedDashboard || !selectedDashboard.rows || Object.keys(currentWidgets).length === 0) {
      return [];
    }

    return selectedDashboard.rows.map(widgetRow => {
      return widgetRow.widgets.map(widget => currentWidgets[widget.id]);
    });
  };

  const getSortedWidgets = () => {
    if (!selectedDashboard || !selectedDashboard.rows || Object.keys(currentWidgets).length === 0) {
      return {};
    }

    const sortedWidgets = [];

    selectedDashboard.rows.forEach(widgetRow => {
      widgetRow.widgets.forEach(widget => {
        sortedWidgets.push(currentWidgets[widget.id] || { id: widget.id });
      });
    });

    return sortedWidgets;
  };

  const getWidgetCount = () => {
    if (!selectedDashboard || !selectedDashboard.rows) return 0;

    return selectedDashboard.rows.map(row => row.widgets.length).reduce((sum, prev) => sum + prev);
  };

  const getCmsPlayerIds = (selectedAssets = []) => {
    return selectedAssets.flatMap(asset => asset.cmsPlayerIds || []).filter(Boolean);
  };

  //#endregion

  //#region Loaders
  const loadSolution = async () => {
    if (project && selectedDashboard) {
      await API.getTableSchema(project.id, selectedDashboard.table)
        .then(res => {
          setSelectedSolution({
            id: selectedDashboard.headers,
            schema: res.schema,
            isCms: selectedDashboard.isCms,
            table: selectedDashboard.table
          });
        })
        .catch(error => {
          console.error(error);
          return [];
        })
        .finally(() => {
          setLoadingDashboard(false);
        });
    }
    return [];
  };

  const loadWidgets = () => {
    // Load Widgets
    const customWidgets =
      user?.preferences?.customWidgets && selectedDashboard
        ? user?.preferences?.customWidgets[selectedDashboard.id]
        : null;
    const newFilteredWidgets = getFilteredWidgets(getWidgetsInPlace(), customWidgets);

    setFilteredWidgets(newFilteredWidgets);
  };

  const loadAllSites = async () => {
    if (!project || !selectedDashboard) return;

    try {
      const response = await API.getAllSitesWithAssets(project.id);
      const compatibleDatasources = new Set(selectedDashboard.compatibleDatasources || []);
      const cmsId = selectedDashboard.cmsId?.toUpperCase();
      const sites = (response.sites || []).filter(site => {
        const hasCompatibleDatasource = (site.datasources || []).some(datasource => compatibleDatasources.has(datasource));
        const hasCompatibleCms = !cmsId || (site.cmsVendors || []).includes(cmsId);
        return hasCompatibleDatasource && hasCompatibleCms;
      });
      const assets = sites.flatMap(site =>
        (site.assets || []).map(asset => ({
          ...asset,
          displayName: asset.displayName || asset.id,
          siteId: site.id
        }))
      );

      setSitesProjectId(project.id);
      setSites(sites);
      setAssets(assets);
      setSiteIds(sites.map(site => site.id));
    } catch (error) {
      console.error('Error loading Sites with Assets:', error);
      setSites([]);
      setAssets([]);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const loadDevices = (availableAssets = []) => {
    const siteIds = new Set(sites.map(site => site.id));
    const devicesById = new Map();

    availableAssets.forEach(asset => {
      if (!siteIds.has(asset.siteId)) return;
      (asset.devices || []).forEach(device => devicesById.set(device.id, device));
    });
    const localDevices = Array.from(devicesById.values()).map(device => ({
      ...device,
      projectId: project.id,
      solution: selectedDashboard.headers
    }));
    setDevices(localDevices);
    setHeatmapDevices(localDevices.filter(device => device.heatmapEnabled));
    setWifiDevices(localDevices.filter(device => device.hasWifi));

    setAppliedFilters({
      ...appliedFilters,
      selectedDevices: localDevices,
      selectedSites: sites,
      sites: sites.map(site => site.id),
      selectedSolution
    });
  };

  //#endregion

  //#region Callbacks
  const onFilterSelect = payload => {
    const newAppliedFilters = utils.deepClone(appliedFilters);
    Object.keys(payload).forEach(key => {
      newAppliedFilters[key] = payload[key];
      // Update mobile data params
      if (key === 'startDate') {
        newAppliedFilters['startTime'] = payload[key];
      }
      if (key === 'endDate') {
        newAppliedFilters['endTime'] = payload[key];
      }
    });

    // If selectedAssets is being updated, also compute cmsPlayerIds at the same time
    if (payload.selectedAssets !== undefined) {
      const cmsPlayerIds = getCmsPlayerIds(payload.selectedAssets || []);
      newAppliedFilters.cmsPlayerIds = cmsPlayerIds;
    }

    setAppliedFilters(newAppliedFilters);
  };

  const onCustomWidgetsChange = customWidgets => {
    const newFilteredWidgets = getFilteredWidgets(getWidgetsInPlace(), customWidgets);
    setFilteredWidgets(newFilteredWidgets);
  };

  const onError = e => {
    const { code, message } = e.detail;
    let errorMessage = message ? message : 'Something went wrong. Please try again later.';

    if (code === 5) {
      errorMessage = 'Integration is not active for your project. Activate it on Integration Settings';
      setHasTable(false);
    }

    dispatch(setAlert(utils.generateAlert(errorMessage ? errorMessage : 'Something went wrong.', 'error')));
  };
  //#endregion

  //#region Renders
  const renderEmptyMessage = () => {
    return (
      <div className="no-data-message flex-column">
        <h1>No data found!</h1>
        <p>Nothing to see here. Try choosing another site group or creating a new one.</p>
      </div>
    );
  };

  const renderTopFilterBar = () => {
    if (!appliedFilters) {
      return null;
    }

    return (
      <div className="filter-bar">
        <DateTimeFilterBar
          appliedFilters={{
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            interval: appliedFilters.interval
          }}
          onPeriodSelected={onFilterSelect}
        />
        <div className="d-flex">
          <GenerateReportButton
            project={project}
            report={reportUtils.getCustomReportTemplate(appliedFilters, user?.email)}
            selectedTemplate={selectedDashboard}
          />
          <CustomWidgets
            defaultWidgets={getSortedWidgets()}
            selectedDashboard={selectedDashboard}
            onChange={onCustomWidgetsChange}
            filters={appliedFilters}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    const isMobileTemplate =
      selectedDashboard &&
      selectedDashboard.compatibleDatasources &&
      selectedDashboard?.compatibleDatasources?.includes('MOBILE_M');
    if (!selectedDashboard) {
      return null;
    }

    if ((devices?.length === 0 && !isMobileTemplate) || !hasTable) {
      return renderEmptyMessage();
    }

    if ((sites?.length === 0 && isMobileTemplate) || !hasTable) {
      return renderEmptyMessage();
    }

    return (
      <div className="d-flex">
        <SidePanel
          isOpen={false}
          content={
            <AnalyticsSelector
              sites={sites}
              selectedDashboard={selectedDashboard}
              devices={devices}
              assets={assets}
              appliedFilters={{
                startDate: appliedFilters.startDate,
                endDate: appliedFilters.endDate,
                timezone: appliedFilters.timezone,
                filterByWeekday: appliedFilters.filterByWeekday,
                filterByTime: appliedFilters.filterByTime,
                selectedSites: appliedFilters.selectedSites,
                selectedDevices: appliedFilters.selectedDevices,
                selectedAdvertisers: appliedFilters.selectedAdvertisers,
                selectedMedias: appliedFilters.selectedMedias,
                selectedAssets: appliedFilters.selectedAssets
              }}
              onSelection={onFilterSelect}
              cmsIntegration={selectedDashboard.isCms ? selectedDashboard.cmsId :
                (selectedDashboard.compatibleDatasources?.includes('MOBILE_M') ?
                  getProjectCmsVendors(project, sitesProjectId === project?.id ? sites : []) : null)}
            />
          }
          onToggle={() => setCollapseSidebar(!collapseSidebar)}
        />
        <div className="scrollable-container">
          <div id="analytics-chart-container" className={`print ${!collapseSidebar ? 'expanded-sidebar' : ''}`}>
            <WidgetGrid
              appliedFilters={appliedFilters} //this uses solution
              filteredWidgets={[
                ...filteredWidgets,
                ...getCmsImpressionWidgets({
                  dashboard: selectedDashboard,
                  project,
                  templates: analyticsTemplates,
                  sites: sitesProjectId === project?.id ? sites : []
                }).filter(widget => !filteredWidgets.flat().some(existing =>
                  existing?.id === widget.id || (existing?.viewId === widget.viewId &&
                    existing?.cmsId?.toLowerCase() === widget.cmsId)
                )).map(widget => [widget])
              ]}
              handleError={onError}
              eventHandlers={eventHandlers.current}
              lastUpdated={lastUpdated}
            />
          </div>
        </div>
      </div>
    );
  };
  //#endregion

  return (
    <div className="parent-container analytics-container">
      {/* We are downloading the image so when the user generates the PDF the image already exists for the browser */}
      <div className="company-logo-img visibility-hidden" />
      <HeaderPDF filters={appliedFilters} devices={devices} />
      <div id="analytics-widget-container" className="d-flex flex-column">
        {renderTopFilterBar()}
        {!loadingDashboard || !project ? (
          renderContent()
        ) : (
          <div className="h-100">
            <Loading size={8} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsContainer;
